import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import type { SupportSessionSummary } from '@br/protocol';
import { errorMessage } from '../api/client';
import { useProfiles } from '../api/queries';
import { RuntimeBadge, StatusBadge } from '../components/badges';
import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  Dot,
  EmptyState,
  FullPageSpinner,
  Input,
  PageHeader,
} from '../components/ui';
import { formatRelative } from '../lib/format';
import { usePageTitle } from '../lib/title';
import { ProfileCreateDialog } from './ProfileCreateDialog';

function SupportSessionBadge({ session }: { session: SupportSessionSummary | null }) {
  if (!session) return <span className="text-ink-muted">없음</span>;
  if (session.revokedAt) return <Badge tone="muted">폐기됨</Badge>;
  if (new Date(session.expiresAt).getTime() <= Date.now())
    return <Badge tone="muted">만료됨</Badge>;
  return <StatusBadge status={session.status} />;
}

export function ProfilesPage() {
  usePageTitle('브라우저 프로필');
  const [params, setParams] = useSearchParams();
  const includeArchived = params.get('archived') === 'true';
  const page = Math.max(1, Number(params.get('page')) || 1);
  const query = params.get('q') ?? '';
  const [search, setSearch] = useState(query);
  useEffect(() => setSearch(query), [query]);
  const profiles = useProfiles(includeArchived, page, query);
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);

  return (
    <div>
      <PageHeader
        title="브라우저 프로필"
        description="고객의 로그인 상태와 쿠키를 유지하는 Chrome 프로필입니다. 하나의 프로필에서 여러 지원 세션을 운영할 수 있습니다."
        actions={
          <>
            <Checkbox
              label="보관된 프로필 포함"
              checked={includeArchived}
              onChange={(event) =>
                setParams({ archived: String(event.target.checked), q: query, page: '1' })
              }
            />
            <Button variant="primary" onClick={() => setCreating(true)}>
              새 프로필
            </Button>
          </>
        }
      />
      {profiles.isPending && <FullPageSpinner />}
      <form
        className="mb-4 flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          setParams({ archived: String(includeArchived), q: search.trim(), page: '1' });
        }}
      >
        <Input
          aria-label="프로필 검색"
          placeholder="프로필 이름, 고객 이름 또는 ID 검색"
          maxLength={120}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Button type="submit">검색</Button>
      </form>
      {profiles.isError && <Alert level="error">{errorMessage(profiles.error)}</Alert>}
      {profiles.data && (
        <Card padded={false}>
          {profiles.data.profiles.length === 0 ? (
            <EmptyState
              title={query ? '검색 결과가 없습니다' : '브라우저 프로필이 없습니다'}
              hint={
                query
                  ? '다른 이름이나 고객 정보로 검색해보세요'
                  : '오른쪽 위 ‘새 프로필’로 시작하세요'
              }
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-canvas text-left text-xs text-ink-muted">
                  <tr>
                    <th className="px-4 py-2 font-medium">프로필 / 고객</th>
                    <th className="px-4 py-2 font-medium">최근 지원 세션</th>
                    <th className="px-4 py-2 font-medium">브라우저</th>
                    <th className="px-4 py-2 font-medium">노드</th>
                    <th className="px-4 py-2 font-medium">참여</th>
                    <th className="px-4 py-2 font-medium">제어자</th>
                    <th className="px-4 py-2 font-medium">생성</th>
                    <th className="px-4 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {profiles.data.profiles.map((profile) => {
                    const archived = Boolean(profile.archivedAt);
                    return (
                      <tr
                        key={profile.id}
                        className="cursor-pointer border-t border-line hover:bg-canvas"
                        onClick={() => navigate(`/profiles/${profile.id}`)}
                      >
                        <td className="px-4 py-2.5">
                          <div className="flex items-center gap-2 font-medium text-ink">
                            <span>{profile.name}</span>
                            {archived && <Badge tone="muted">보관됨</Badge>}
                          </div>
                          {profile.customerLabel && (
                            <div className="text-xs text-ink-muted">{profile.customerLabel}</div>
                          )}
                        </td>
                        <td className="px-4 py-2.5">
                          <SupportSessionBadge session={profile.latestSupportSession} />
                        </td>
                        <td className="px-4 py-2.5">
                          <RuntimeBadge runtime={profile.runtime} />
                        </td>
                        <td className="px-4 py-2.5">
                          <span className="inline-flex items-center gap-1.5">
                            <Dot online={profile.nodeOnline} />
                            {profile.nodeName}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 tabular-nums">{profile.participantCount}명</td>
                        <td className="px-4 py-2.5">
                          {profile.controllerName ?? <span className="text-ink-muted">-</span>}
                        </td>
                        <td className="px-4 py-2.5 text-ink-muted" title={profile.createdAt}>
                          {formatRelative(profile.createdAt)}
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          {!archived && (
                            <Link
                              to={`/profiles/${profile.id}/view`}
                              onClick={(event) => event.stopPropagation()}
                              className="text-xs font-medium text-primary hover:underline"
                            >
                              뷰어
                            </Link>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}
      <ProfileCreateDialog open={creating} onClose={() => setCreating(false)} />
      {profiles.data && (
        <div className="mt-4 flex items-center justify-between gap-3 text-sm">
          <span>
            총 {profiles.data.total}개 · {page} /{' '}
            {Math.max(1, Math.ceil(profiles.data.total / profiles.data.pageSize))} 페이지
          </span>
          <div className="flex gap-2">
            <Button
              disabled={page <= 1}
              onClick={() =>
                setParams({ archived: String(includeArchived), q: query, page: String(page - 1) })
              }
            >
              이전
            </Button>
            <Button
              disabled={page * profiles.data.pageSize >= profiles.data.total}
              onClick={() =>
                setParams({ archived: String(includeArchived), q: query, page: String(page + 1) })
              }
            >
              다음
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
